import { inject, Injectable } from '@angular/core';
import { LocalStorageService } from '@vectronic/util';
import { Config, Driver, driver, DriveStep } from 'driver.js';
import { BehaviorSubject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class TourGuideService {
  id: string;
  driver: Driver;
  localStorageService = inject(LocalStorageService);
  destroyedId$ = new BehaviorSubject<string>(undefined);
  previouslyShownIds: string[] = [];

  isActive() {
    return this.driver?.isActive();
  }

  createTour(config: Config, id: string, onHighlightStarted?: (step: DriveStep) => void) {
    this.driver?.destroy();
    config.onPopoverRender = (popover) => {
      const div = document.createElement('div');
      div.className = 'tour-guide-never-div';
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.id = 'tour-guide-never-checkbox';
      checkbox.checked = this.localStorageService.get(`tour-guide-${this.id}`) === 'true';

      const label = document.createElement('label');
      label.htmlFor = 'tour-guide-never-checkbox';
      label.innerText = "Don't show again";
      popover.description.appendChild(div);
      popover.description.appendChild(checkbox);
      popover.description.appendChild(label);

      checkbox.addEventListener(
        'change',
        (event) => {
          if (event?.cancelable) {
            event.preventDefault();
          }
          if ((event.target as any).checked) {
            this.localStorageService.set(`tour-guide-${this.id}`, 'true', false);
          } else {
            this.localStorageService.set(`tour-guide-${this.id}`, 'false', false);
            sessionStorage.setItem(`tour-guide-${this.id}`, 'false');
          }
        },
        { passive: false },
      );
    };

    config.steps.forEach((step) => {
      if (onHighlightStarted != null) {
        step.onHighlightStarted = () => onHighlightStarted?.(step);
      }
      if (step.popover.onNextClick != null) {
        const originalHandler = step.popover.onNextClick;
        step.popover.onNextClick = (...args) => {
          // Call the original handler
          originalHandler(...args);

          // set timeout so that it waits for the animation to finish
          setTimeout(() => this.driver.moveNext(), 0);
        };
      }
    });
    config.onDestroyed = () => {
      this.previouslyShownIds.push(id);
      this.destroyedId$.next(id);
      if (sessionStorage.getItem(`tour-guide-${id}`) === 'false') {
        sessionStorage.removeItem(`tour-guide-${id}`);
      } else {
        sessionStorage.setItem(`tour-guide-${id}`, 'true');
      }
    };
    this.driver = driver(config);
    this.id = id;
  }

  addNextStep(step: DriveStep, onHighlightStarted?: (step: DriveStep) => void) {
    if (!this.driver.isActive()) return;
    const currentStepIndex = this.driver.getActiveIndex();
    const config = this.driver.getConfig();

    const previousSteps = config.steps?.filter((s) => s.element === step.element);
    const updatedSteps = [
      ...previousSteps.slice(0, currentStepIndex + 1),
      step,
      ...previousSteps.slice(currentStepIndex + 1),
    ];
    step.onHighlightStarted = () => onHighlightStarted?.(step);

    const onDestroyed = config.onDestroyed;
    config.onDestroyed = undefined;
    this.driver.destroy();
    config.onDestroyed = onDestroyed;
    this.driver.setSteps(updatedSteps);
    this.driver.moveTo(currentStepIndex + 1);
  }

  addStepsToEnd(steps: DriveStep[], onHighlightStarted?: (step: DriveStep) => void) {
    if (!this.driver.isActive()) return;

    const currentStepIndex = this.driver.getActiveIndex();
    const config = this.driver.getConfig();
    const previousSteps = config.steps?.filter(
      (step) => !steps.some((s) => s.element === step.element),
    );
    steps.forEach((step) => {
      step.onHighlightStarted = () => onHighlightStarted?.(step);
    });
    const updatedSteps = [...previousSteps, ...steps];
    const onDestroyed = config.onDestroyed;
    config.onDestroyed = undefined;
    this.driver.destroy();
    config.onDestroyed = onDestroyed;
    this.driver.setSteps(updatedSteps);
    this.driver.moveTo(currentStepIndex);
  }

  startTour() {
    if (!this.driver.isActive()) {
      this.driver?.drive();
    }
  }

  isShown() {
    return this.isShownById(this.id);
  }

  isShownById(id: string) {
    return (
      this.localStorageService.get(`tour-guide-${id}`) === 'true' ||
      !!sessionStorage.getItem(`tour-guide-${id}`)
    );
  }

  setUnshownById(id: string) {
    this.localStorageService.set(`tour-guide-${id}`, 'false', false);
  }

  setUnshown() {
    this.setUnshownById(this.id);
  }
}
